import React, { EventHandler } from 'react';
import classNames from 'classnames';
import './styles/index.scss';
import * as dataURIs from '../case-detail/_data-uris';
import { InfoCell, UNEDITABLE_FIELD } from '../info-cell/info-cellv2';
import { Card, LoadingSlugs } from '@codametrix/ui-components';
import { propAccessor2 } from '../case-prop-accessor/case-prop-accessor2';
import { propAccessor } from '../case-prop-accessor/case-prop-accessor';
import { PropertyAccessor } from '../case-prop-accessor/propery-accessor';
import { DataTransformers } from './encounter-data.transformers';
import { Chevron } from '@codametrix/ui-components';
import {
  caseUtils,
  conditionalUtils,
  AmplifyCore
} from '@codametrix/ui-common';
import { Conditional } from '@codametrix/ui-components';
import { CMXIcon } from '@codametrix/ui-components';
import { FlagItemList } from '../flag-items/FlagItemList';

const { getCoderNotesLookupParams } = AmplifyCore.coderNotesUtils;
export const DataStyles = {
  s: 'cmx-col-3 cmx-col-lg-4 cmx-col-md-4',
  ms: 'cmx-col-4 cmx-col-md-6',
  m: 'cmx-col-8',
  l: 'cmx-col-12'
};

const getExtraCaseInformationEntries = (
  extraCaseInformation?: Record<string, unknown>
) => {
  return Object.entries(extraCaseInformation ?? {})
    .filter(([key, value]) => {
      return (
        key.trim().length > 0 &&
        value !== null &&
        value !== undefined &&
        String(value).trim().length > 0
      );
    })
    .sort(([leftKey], [rightKey]) =>
      leftKey.localeCompare(rightKey, undefined, { sensitivity: 'base' })
    )
    .map(([key, value]) => [key, String(value)] as [string, string]);
};

const getExtraCaseInformation = (
  workItem?: CMxCommonApp.WorkItem,
  activeItem?: CMxAPI.CaseRun
): Record<string, unknown> | undefined => {
  return (
    workItem?.extraCaseInformation ??
    (activeItem as any)?.extraCaseInformation ??
    (activeItem as any)?.extra_case_information
  );
};

type OpenClaim = {
  fieldName: any;
  fieldIdentifier: any;
};

type PatientEncounterProps = {
  activeItem: CMxAPI.CaseRun;
  handleExpansion?: (expanded: boolean) => void;
  showCoderNotesInfo?: (
    coderNotesParams: CMxCommonApp.CoderNotesParams
  ) => void;
  showProviderInfo?: (providerInfo: CMxCommonApp.ProviderSearchParams) => void;
  propertyConfig?: CMxCommonApp.PatientInformationConfiguration;
  showLoading?: boolean;
  openClaims?: OpenClaim[];
  activations?: CMxCommonApp.UserActivations;
  fieldEditorInfo: CMxAPI.FieldEditorInfo;
  capability?: CMxCommonApp.Capability;
  coderNotesCapability?: CMxCommonApp.Capability;
  flagList?: CMxAPI.FlagItem[];
  workItem?: CMxCommonApp.WorkItem;

  activeContext?: CMxAPI.OrganizationContext;
};

type PatientEncounterState = {
  expandEncounter: boolean;
};

export const CellMaker = (
  propAccessor: PropertyAccessor,
  rawCaseRun: CMxAPI.CaseRun,
  fieldEditorInfo: CMxAPI.FieldEditorInfo,
  capability: CMxCommonApp.Capability,
  showProviderInfo?: (providerInfo: CMxCommonApp.ProviderSearchParams) => void,
  activeContext?: CMxAPI.OrganizationContext
) => {
  const dataTransformer = DataTransformers(
    propAccessor,
    rawCaseRun,
    activeContext
  );

  return (def: CMxCommonApp.PatientDataDefinition) => {
    const cellClassName = DataStyles[def.dataSizeHorizontalView];
    let value = def.transformer
      ? dataTransformer[def.transformer](def.property)
      : propAccessor(def.property, 'N/A');

    const additionalInfo = def?.additionalInfo?.reduce(
      (acc, info: CMxCommonApp.AdditionalPatientDataDef) => {
        const key = info?.label;
        const value = info.transformer
          ? dataTransformer[info.transformer](info.property)
          : propAccessor(info.property, 'N/A');

        // Ensure both key and value are strings
        acc[key] = value?.toString();
        return acc;
      },
      {} as Record<string, any>
    ); // Initialize an empty object with string key-value types

    if ((value == 'N/A' || value == undefined) && def.defaultHide) {
      return null;
    }
    const _handleUpdate = (updates: any) => {
      const { caserun_uid, service_line } = rawCaseRun;
      const caseEdit = fieldEditorInfo.caseEdits?.[def.key];
      const initialVal = caseEdit?.initialVal;
      const mappedValues = caseEdit?.mappedValues;
      const caseUpdates = { ...updates };
      if (caseUpdates.hasOwnProperty('patient_type')) {
        const patientTypeValue = caseUpdates['patient_type'];
        const mappedValues =
          fieldEditorInfo.caseEdits['patient_class']?.mappedValues || [];
        const value = mappedValues.find(
          (item) => item.label === patientTypeValue
        )?.value;
        if (value !== undefined) {
          caseUpdates['patient_class'] = value;
        }
      }

      fieldEditorInfo.updateCase({
        caseId: rawCaseRun.case_id,
        caseUid: rawCaseRun.case_uid,
        caseRunUid: caserun_uid,
        serviceLine:
          service_line == 'Inpatient Professional Coding'
            ? 'ipc'
            : service_line,
        caseUpdates,
        initialVal,
        mappedValues
      });
    };
    return (
      <InfoCell
        key={`${def.key}`}
        className={cellClassName}
        cellKey={def.key}
        label={def.label}
        value={value}
        fieldType={def.fieldType}
        fieldEditorInfo={{
          ...fieldEditorInfo,
          updateCase: (updates) => _handleUpdate(updates)
        }}
        capability={capability}
        showProviderInfo={showProviderInfo}
        hasLink={def?.additionalInfo ? def?.hasLink : false}
        additionalInfo={additionalInfo}
      />
    );
  };
};

export const CellPatientMaker = (
  propAccessor: PropertyAccessor,
  rawCaseRun: CMxAPI.CaseRun
) => {
  const dataTransformer = DataTransformers(propAccessor, rawCaseRun);

  return (def: CMxCommonApp.PatientDataDefinition) => {
    const value = def.transformer
      ? dataTransformer[def.transformer](def.property)
      : propAccessor(def.property, 'N/A');

    return (
      <div key={def.key} className="patient-cell">
        {value}
        {def.addHr ? <div className="vertical-divider"></div> : null}
      </div>
    );
  };
};

class PatientEncounterv2 extends React.Component<
  PatientEncounterProps,
  PatientEncounterState
> {
  static displayName = 'PatientEncounter';
  static defaultProps = {
    openClaims: [],
    showLoading: false,
    activations: {},
    handleExpansion: (expanded: boolean) => {},
    showProviderInfo: (providerInfo: CMxCommonApp.ProviderSearchParams) => {},
    propertyConfig: {
      patient: [],
      summary: [],
      expanded: []
    },
    flagList: [],
    workItem: {
      owner: '',
      assignee: '',
      aging_days: '',
      codable_days: ''
    }
  };

  constructor(props: PatientEncounterProps) {
    super(props);
    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handleCoderNotesClick = this.handleCoderNotesClick.bind(this);
    this.handleCoderNotesKeyDown = this.handleCoderNotesKeyDown.bind(this);
    this.state = {
      expandEncounter: true
    };
  }

  handleToggleClick(ev: React.MouseEvent<HTMLAnchorElement>) {
    ev.preventDefault();
    const { expandEncounter } = this.state;
    let { openClaims } = this.props;

    if (expandEncounter) {
      openClaims = [];
    } else {
      openClaims = [
        ...openClaims,
        { fieldIdentifier: 'showToggle', fieldName: 'showToggle' }
      ];
    }

    this.setState({
      expandEncounter: !expandEncounter
    });
    this.props.handleExpansion(!expandEncounter);
  }

  expandEncounter(expandEncounter: boolean) {
    this.setState({
      expandEncounter: expandEncounter
    });
    this.props.handleExpansion(expandEncounter);
  }

  /**
   * Handles the click event on the "Coder Notes" section.
   * Prevents default behavior and triggers the coder notes info display.
   *
   * @param {React.MouseEvent<HTMLDivElement>} ev - The mouse event object.
   * @returns {void}
   */
  handleCoderNotesClick(ev: React.MouseEvent<HTMLDivElement>) {
    const activeItem = this.props.activeItem;
    const lookupParams = getCoderNotesLookupParams(activeItem);
    ev.preventDefault();
    this.props.showCoderNotesInfo({
      ...lookupParams,
      patientMRN: activeItem?.patient_identifier?.number,
      serviceLine: activeItem?.service_line
    });
  }

  /**
   * Handles the keydown event on the "Coder Notes" section.
   * Triggers the coder notes info display when the Enter key is pressed.
   *
   * @param {React.KeyboardEvent<HTMLDivElement>} ev - The keyboard event object.
   * @returns {void}
   */
  handleCoderNotesKeyDown(ev: React.KeyboardEvent<HTMLDivElement>) {
    const activeItem = this.props.activeItem;
    const lookupParams = getCoderNotesLookupParams(activeItem);
    if (ev.key === 'Enter') {
      ev.preventDefault();
      this.props.showCoderNotesInfo({
        ...lookupParams,
        patientMRN: activeItem?.patient_identifier?.number,
        serviceLine: activeItem?.service_line
      });
    }
  }

  render(): React.ReactNode {
    const classnames = ['patient-encounterv2'];
    const { expandEncounter } = this.state;
    const {
      activations,
      activeItem,
      propertyConfig,
      fieldEditorInfo,
      capability,
      flagList,
      coderNotesCapability,
      workItem
    } = this.props;

    const propActivations = activations?.property ?? {};

    const prop = propAccessor2(
      {
        ...activeItem,
        ...workItem
      },
      activations.property
    );

    const cellMaker = CellMaker(
      prop,
      activeItem,
      fieldEditorInfo,
      capability,
      this.props.showProviderInfo,
      this.props.activeContext
    );
    const cellPatientMaker = CellPatientMaker(prop, activeItem);

    // all info less last item
    let patientConfig = propertyConfig.patient.slice(
      0,
      propertyConfig.patient.length - 1
    );
    // last item
    const patientLastItem = propertyConfig.patient.slice(-1);

    // add hr less the last one item
    patientConfig = patientConfig.map((patient, index) => {
      return {
        ...patient,
        addHr: index + 1 !== patientConfig.length
      };
    });

    const midCells = propertyConfig.summary.map(cellMaker);
    const extraCaseInformationEntries = getExtraCaseInformationEntries(
      getExtraCaseInformation(workItem, activeItem)
    );

    const showComments = conditionalUtils.capabilityValue(
      coderNotesCapability?.on ?? false
    );

    return (
      <div className={classNames(classnames)}>
        <div
          className="patient-info-container"
          data-testid="patient-info-container">
          <Conditional every={[flagList?.length > 0]}>
            <div
              className={`flex-section ${
                caseUtils.getStatusReason(activeItem) ? 'zeroTopPadding' : ''
              }`}>
              <FlagItemList flagList={flagList} />
            </div>
          </Conditional>

          {(propertyConfig.summary.length > 0 ||
            extraCaseInformationEntries.length > 0) && (
            <>
              {extraCaseInformationEntries.length > 0 && (
                <div className="info-block extra-case-information-block">
                  {extraCaseInformationEntries.map(([label, value], index) => (
                    <InfoCell
                      key={`extra-case-information-${index}-${label}`}
                      className={DataStyles.l}
                      cellKey={`extra_case_information_${index}`}
                      label={label}
                      value={value}
                      fieldEditorInfo={{ ...UNEDITABLE_FIELD }}
                    />
                  ))}
                </div>
              )}
              {propertyConfig.summary.length > 0 && (
                <div className="info-block">{midCells}</div>
              )}
            </>
          )}
        </div>
        <Conditional every={[showComments]}>
          <div
            tabIndex={0}
            onClick={this.handleCoderNotesClick}
            onKeyDown={this.handleCoderNotesKeyDown}
            className="coder-comments-section">
            <CMXIcon
              svgIconClass="coder-commentsv2"
              svgIconName="BiomeComments"
              svgIconSize="24"
            />
          </div>
        </Conditional>
      </div>
    );
  }
}

export { PatientEncounterv2 };
